export default class Utils {
  /**
   * Get item from localStorage
   * @param key - The key to identify the item in localStorage
   * @returns Parsed JSON data from localStorage, or null if the key does not exist
   */
  static localStorageGetItem<T>(key: string): T | null {
    const data = localStorage.getItem(key);
    return data ? (JSON.parse(data) as T) : null;
  }

  /**
   * Set item in localStorage
   * @param key - The key to identify the item in localStorage
   * @param data - Data to be stored in localStorage (will be JSON.stringify-ed)
   */
  static localStorageSetItem<T>(key: string, data: T): void {
    localStorage.setItem(key, JSON.stringify(data));
  }

  /**
   * Remove item from localStorage
   * @param key - The key to identify the item in localStorage
   */
  static localStorageRemoveItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Handle success and error callback for promises
   * @param promise - The promise to be handled
   * @returns A promise that resolves to an array with either [null, data] on success or [err] on failure
   */
  static async parseResponse<T>(promise: Promise<T>): Promise<[null, T] | [Error]> {
    try {
      const data = await promise;
      return [null, data];
    } catch (err) {
      return [err as Error];
    }
  }

  /**
   * Generate a GUID (Globally Unique Identifier)
   * @returns A string representing a GUID
   */
  static guid(): string {
    const s4 = (): string =>
      Math.floor(Math.random() * 65536)
        .toString(16)
        .padStart(4, '0');

    return `${s4()}${s4()}-${s4()}-${'4'}${s4().substring(1)}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  /**
   * Add days to a given date
   * @param date - The date object to modify
   * @param days - The number of days to add
   * @returns A new Date object with the added days
   */
  static addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }

  static getDataFromLocalStorage<T>(key: string): T | null {
    const data = localStorage.getItem(key);
    return data ? (JSON.parse(data) as T) : null;
  }

  static storeToLocalStorage<T>(key: string, data: T): void {
    localStorage.setItem(key, JSON.stringify(data));
  }

  static capitalizeFirstLetter(str: string) {
    if (!str) return;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  static formatCreditsDecimal(credits: number): string | number {
    if (Math.abs(credits - Math.round(credits)) > 0.5e-5) {
      return credits.toFixed(5).replace(/\.?0+$/, '');
    } else {
      return credits.toString();
    }
  }

  static formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  }

  static numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Save access and refresh tokens as cookies
   * @param access - Access token
   * @param refresh - Refresh token
   */
  static saveTokensToCookies(access: string, refresh: string): void {
    const now = Date.now();
    const twoHoursLater = new Date(now + 24 * 60 * 60 * 1000).toUTCString();
    const sevenDaysLater = new Date(now + 7 * 24 * 60 * 60 * 1000).toUTCString();

    document.cookie = `auth-token=${access}; path=/; expires=${twoHoursLater}; secure; SameSite=Strict`;
    document.cookie = `refresh-auth-token=${refresh}; path=/; expires=${sevenDaysLater}; secure; SameSite=Strict`;
    document.cookie = `expires-in=${twoHoursLater}; path=/; expires=${twoHoursLater}; secure; SameSite=Strict`;
  }

  /**
   * Retrieve a cookie value by name
   * @param name - Cookie name
   * @returns The cookie value or null if not found
   */
  static getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Clear access and refresh tokens from cookies
   */
  static clearTokenCookies(): void {
    const expireNow = 'Thu, 01 Jan 1970 00:00:00 UTC';
    document.cookie = `auth-token=; path=/; expires=${expireNow}; secure; SameSite=Strict`;
    document.cookie = `refresh-auth-token=; path=/; expires=${expireNow}; secure; SameSite=Strict`;
    document.cookie = `expires-in=; path=/; expires=${expireNow}; secure; SameSite=Strict`;
  }
}
